/** Investigate substate: pathfind toward a corpse and stop at a random distance. */
import { TILE_SIZE } from '../../utils/Constants';
import { findPath } from '../../utils/Pathfinder';
import type { Position } from '../../utils/Types';
import { MovementResult, ZERO_MOVEMENT, INVESTIGATE_STOP_MIN_TILES, INVESTIGATE_STOP_MAX_TILES } from '../constants';
import { randomInRange, distanceSq } from '../../utils/Math';
import type { PathHelper } from '../PathHelper';

/** Mutable investigate state owned by DutySystem. */
export interface InvestigateState {
  targetX: number;
  targetY: number;
  stopDistancePx: number;
  path: Position[] | null;
  pathIndex: number;
  arrived: boolean;
}

/** Create a fresh investigate state (inactive). */
export function createInvestigateState(): InvestigateState {
  return { targetX: 0, targetY: 0, stopDistancePx: 0, path: null, pathIndex: 0, arrived: false };
}

/** Initialize an investigation toward a corpse position with a randomized stop distance. */
export function setInvestigateTarget(state: InvestigateState, x: number, y: number): void {
  state.targetX = x;
  state.targetY = y;
  state.stopDistancePx = randomInRange(
    INVESTIGATE_STOP_MIN_TILES * TILE_SIZE,
    INVESTIGATE_STOP_MAX_TILES * TILE_SIZE,
  );
  state.path = null;
  state.pathIndex = 0;
  state.arrived = false;
}

/**
 * Advance toward the corpse. Stops at stopDistancePx and sets arrived=true.
 * Returns movement result.
 */
export function investigateMovement(
  state: InvestigateState,
  entityX: number, entityY: number,
  moveSpeed: number,
  groundLayer: Phaser.Tilemaps.TilemapLayer,
  pathHelper: PathHelper,
  logs: string[],
): MovementResult {
  if (state.arrived) return ZERO_MOVEMENT;

  // Check if we're close enough to stop
  if (distanceSq(entityX, entityY, state.targetX, state.targetY) <= state.stopDistancePx * state.stopDistancePx) {
    state.arrived = true;
    logs.push(`INVESTIGATE arrived near corpse (${Math.round(state.targetX)},${Math.round(state.targetY)}) stopDist=${Math.round(state.stopDistancePx)}`);
    return ZERO_MOVEMENT;
  }

  // Compute path if we don't have one
  if (!state.path) {
    const { path: pathResult } = findPath(groundLayer, entityX, entityY, state.targetX, state.targetY);
    if (!pathResult || pathResult.length === 0) {
      state.arrived = true;
      logs.push(`INVESTIGATE unreachable corpse (${Math.round(state.targetX)},${Math.round(state.targetY)})`);
      return ZERO_MOVEMENT;
    }
    state.path = pathResult;
    state.pathIndex = 0;
  }

  // Follow waypoints
  const wp = pathHelper.followWaypoints(state.path, state.pathIndex, entityX, entityY, moveSpeed);
  state.pathIndex = wp.newIndex;

  if (wp.arrived) {
    state.arrived = true;
    logs.push(`INVESTIGATE path complete near corpse (${Math.round(state.targetX)},${Math.round(state.targetY)})`);
    return ZERO_MOVEMENT;
  }

  return { vx: wp.vx, vy: wp.vy, wantsAttack: false };
}
